// Admin JWT storage + axios interceptor wiring.
//
// Responsibilities:
//  - Persist the access token in localStorage so page reloads stay signed in.
//  - Attach `Authorization: Bearer <token>` to every outgoing request.
//  - On any 401 response from an /api/admin/* endpoint, clear the token and
//    redirect the user to the login page.
//
// Call `installAdminAuthInterceptor()` exactly once at app bootstrap.
import axios from "axios";

const TOKEN_KEY = "assenda_admin_token";
const EXPIRY_KEY = "assenda_admin_token_expires_at";

// --- token storage ------------------------------------------------------

export const getAdminToken = () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiresAt = localStorage.getItem(EXPIRY_KEY);
    if (!token) return null;
    if (expiresAt && Date.parse(expiresAt) <= Date.now()) {
      // Token already expired — clean up proactively.
      clearAdminToken();
      return null;
    }
    return token;
  } catch {
    return null;
  }
};

export const setAdminToken = ({ access_token, expires_at }) => {
  try {
    localStorage.setItem(TOKEN_KEY, access_token);
    if (expires_at) localStorage.setItem(EXPIRY_KEY, expires_at);
  } catch {
    /* localStorage unavailable */
  }
};

export const clearAdminToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRY_KEY);
  } catch {
    /* noop */
  }
};

export const isAdminAuthenticated = () => !!getAdminToken();

// --- interceptor -------------------------------------------------------

let installed = false;

export const installAdminAuthInterceptor = () => {
  if (installed) return;
  installed = true;

  // Request: attach bearer token if we have one.
  axios.interceptors.request.use((config) => {
    const token = getAdminToken();
    if (token) {
      config.headers = config.headers || {};
      // Don't clobber an explicitly-set auth header on a specific call.
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  // Response: on 401 from an admin route, log out and bounce to /admin/login.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      const isAdminApi = url.includes("/api/admin/");
      const isLoginCall = url.endsWith("/api/admin/login");
      if (status === 401 && isAdminApi && !isLoginCall) {
        clearAdminToken();
        // Avoid redirect loops if we're already on the login page.
        if (
          typeof window !== "undefined" &&
          !window.location.pathname.startsWith("/admin/login")
        ) {
          window.location.assign("/admin/login");
        }
      }
      return Promise.reject(error);
    }
  );
};
